import { useMemo } from 'react';

import type { EChartsOption } from 'echarts';

import { graphConfig } from '@/configs/Graphs';
import { BehaviorChart } from '@/features/forecasts/components/order-behaviour/BehaviorChart';
import { useOrderData } from '@/features/forecasts/hooks/useOrderData';
import { buildTooltipSeriesLine, joinTooltipLines } from '@/features/forecasts/utils/forecastTooltip';
import { formatIsoDateMd } from '@/utils/date';
import { format } from '@/utils/format';
import { createNoDataOption } from '@/utils/graphs';

const MIN_NON_ZERO_DISPLAY_VALUE = 0.5;

function mapZeroToDisplayValue(value: number | null) {
  if (value == null) return value;
  return value === 0 ? MIN_NON_ZERO_DISPLAY_VALUE : value;
}

type Props = {
  chartGroup?: string;
};

export function OrderBehaviorQty({ chartGroup }: Props) {
  const { records, total } = useOrderData();

  const { axisLabel, grid, colors } = graphConfig;
  const orderCount = format('Last {{total}} orders', { total });
  const average = useMemo(() => {
    const numericQuantities = records
      .map((record) => record.quantity)
      .filter((value): value is number => typeof value === 'number');
    if (!numericQuantities.length) return 'N/A';
    const sum = numericQuantities.reduce((acc, value) => acc + value, 0);
    return Math.round(sum / numericQuantities.length).toLocaleString('en');
  }, [records]);
  const subtitle = format('Average: {{average}} • {{orderCount}}', { average, orderCount });

  const chartOptions = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      /* c8 ignore start */
      formatter: (params) => {
        const p = Array.isArray(params) ? params[0] : params;
        const data = p.data as { value: number; timestamp?: string };
        const dateLabel = formatIsoDateMd(data.timestamp ?? '');
        return joinTooltipLines([
          dateLabel && buildTooltipSeriesLine({ label: 'Date', value: dateLabel }),
          data.value != null && buildTooltipSeriesLine({ label: 'Quantity', value: data.value }),
        ]);
      },
      /* c8 ignore stop */
    },
    grid,
    xAxis: {
      type: 'category',
      // Plotted against order sequence (0,1,2,…) so spacing is even regardless of
      // gaps between order dates; the date stays available in the tooltip.
      data: records.map((d) => `${d.order_sequence ?? ''}`),
      axisLabel,
    },
    yAxis: { type: 'value', axisLabel },
    dataZoom: [{ type: 'slider', filterMode: 'none', bottom: 5, height: 20 }],
    series: [
      {
        type: 'line',
        data: records.map((d) => ({ value: mapZeroToDisplayValue(d.quantity), timestamp: d.date })),
        lineStyle: { color: colors.gray, width: 1.5 },
        itemStyle: { color: colors.gray },
        symbol: 'circle',
        symbolSize: 4,
      },
    ],
  } satisfies EChartsOption;

  return (
    <BehaviorChart
      chartGroup={chartGroup}
      option={total ? chartOptions : createNoDataOption('No data available')}
      title={'Order Quantity'}
      titleInfo={subtitle}
    />
  );
}
